import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { QUERY_KEYS } from 'constants/queryKeys';
import { servicePointsServices } from '@services';
import { showError } from '@utils';
import { DEFAULT_LOCATIONS } from 'hooks/vehicles/constants';
import { SERVICE_POINTS_PAGE_SIZE } from '@constants';

const processServicePointsData = (data = [], defaultLocations = []) => {
  if (!data || data?.length === 0)
    return {
      servicePoints: [],
      servicePointsMap: {},
      servicePointsWithDefaultLocations: defaultLocations,
    };

  const sortedData = [...data].sort((a, b) =>
    (a?.name || '').localeCompare(b?.name || '')
  );

  const servicePoints = [];
  const servicePointsMap = {};

  sortedData.forEach((item) => {
    const primaryContact = item?.contacts?.find(
      (contact) => contact?.isPrimary === true
    );
    const servicePoint = {
      ...item,
      text: item?.name,
      label: item?.name,
      value: item?.id,
      id: item?.id,
      email: primaryContact?.email,
    };
    servicePoints.push(servicePoint);
    if (item?.id) servicePointsMap[item.id] = servicePoint;
  });

  const seen = new Set(defaultLocations.map((loc) => loc.value));
  const servicePointsWithDefaultLocations = [
    ...defaultLocations,
    ...servicePoints.filter((point) => !seen.has(point.value)),
  ];

  return { servicePoints, servicePointsMap, servicePointsWithDefaultLocations };
};

export const useServicePoints = (options = {}) => {
  const queryClient = useQueryClient();

  const queryResult = useQuery(
    QUERY_KEYS.GET_SERVICE_POINTS_KEY,
    () =>
      servicePointsServices.getServicePoints({
        page: 1,
        pageSize: SERVICE_POINTS_PAGE_SIZE,
      }),
    {
      // Increased stale time to 30 minutes to reduce unnecessary rerenders across pages using Service Points, even when no network refetch is triggered.
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      ...options,
    }
  );

  const { servicePoints, servicePointsMap, servicePointsWithDefaultLocations } =
    useMemo(() => {
      return processServicePointsData(
        queryResult?.data?.data,
        DEFAULT_LOCATIONS
      );
    }, [queryResult?.data?.data]);

  const invalidateServicePoints = useCallback(() => {
    queryClient.invalidateQueries(QUERY_KEYS.GET_SERVICE_POINTS_KEY);
  }, [queryClient]);

  useEffect(() => {
    if (queryResult?.isError) {
      showError(
        queryResult?.error ||
          'Error fetching service points. Try refreshing the page.'
      );
    }
  }, [queryResult?.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    servicePoints,
    servicePointsMap,
    servicePointsWithDefaultLocations,
    invalidateServicePoints,
    ...queryResult,
  };
};

export default useServicePoints;
